<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background using login.png -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style="background-image: url('/login.png')"
    >
      <!-- Overlay to ensure text readability -->
      <div class="absolute inset-0 bg-blue-900/20"></div>
    </div>
    
    <!-- Content Container -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-6 py-32 lg:px-8">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 border-4 border-black shadow-[8px_8px_0_rgba(0,0,0,1)]">
        
        <!-- Loading State (checking device limit) -->
        <div v-if="isCheckingLimit" class="text-center py-8">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p class="text-gray-600">Checking device status...</p>
        </div>

        <!-- Main Content (shown after checking) -->
        <div v-else>
        <!-- Header -->
      <div class="text-center mb-6">
        <div v-if="!deviceLimitReached" class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <div v-else class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 v-if="!deviceLimitReached" class="text-2xl font-bold text-gray-900 mb-2">Trust This Device?</h1>
        <h1 v-else class="text-2xl font-bold text-red-900 mb-2">Access Blocked</h1>
        <p v-if="!deviceLimitReached" class="text-sm text-gray-600">
          You won't need to enter a code on future logins from this device for the next 90 days.
        </p>
        <p v-else class="text-sm text-red-700 font-medium">
          You've reached the maximum of 3 trusted devices.
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage && !deviceLimitReached" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <p class="text-sm text-red-700">{{ errorMessage }}</p>
      </div>

      <!-- Device Limit Warning -->
      <div v-if="deviceLimitReached" class="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
        <div class="flex">
          <svg class="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <div class="flex-1">
            <h4 class="text-sm font-semibold text-red-800 mb-1">Maximum Device Limit Reached (3/3)</h4>
          
            <p class="text-xs text-red-700">
              You must remove at least one trusted device before you can continue.
            </p>
          </div>
        </div>
      </div>

      <!-- Device Info (only show if limit not reached) -->
      <div v-if="!deviceLimitReached" class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 class="text-sm font-semibold text-gray-800 mb-2">Device Information:</h4>
        <div class="text-xs text-gray-600 space-y-1">
          <div>• Browser: {{ deviceInfo.browserInfo }}</div>
          <div>• Operating System: {{ deviceInfo.osInfo }}</div>
        </div>
        
      </div>

      <!-- Actions -->
      <div class="flex flex-col space-y-3">
        <!-- Show different buttons based on device limit status -->
        <template v-if="!deviceLimitReached">
          <button
            @click="handleTrustDevice(true)"
            :disabled="isProcessing"
            class="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <span v-if="!isProcessing">Yes, trust this device</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          </button>

          <button
            @click="handleTrustDevice(false)"
            :disabled="isProcessing"
            class="w-full px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            Not now
          </button>
        </template>
        
        <!-- When device limit reached, show remove device button -->
        <template v-else>
          <button
            @click="goToManageDevices"
            class="w-full px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 cursor-pointer transition-colors"
          >
            Remove a Device
          </button>
          <button
            @click="handleLogout"
            class="w-full px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 cursor-pointer transition-colors"
          >
            Logout
          </button>
        </template>
      </div>

      <!-- Security Notice -->
      <div v-if="!deviceLimitReached" class="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex">
          <svg class="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
          </svg>
          <p class="text-xs text-blue-700">
            You can manage trusted devices in your account settings.
          </p>
        </div>
      </div>
      
      <!-- Info about 2FA requirement -->
      <div v-else class="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div class="flex">
          <svg class="h-5 w-5 text-gray-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <p class="text-xs text-gray-700">
            For security, admin accounts require 2FA verification on untrusted devices. Each admin can trust up to 3 devices.
          </p>
        </div>
      </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { twoFactorAuthService } from '@/services/twoFactorAuthService';
import { trustedDeviceService } from '@/services/trustedDeviceService';
import { logDebug, logInfo, logWarn, logError, logSuccess } from '@/utils/secureLogger';
import { createAuditLog } from '@/services/auditLogService';
import { auth } from '@/firebase';

const router = useRouter();
const route = useRoute();

// Props from route params or query
const twoFAId = ref((route.query.twoFAId as string) || (route.params.twoFAId as string));

// State
const isProcessing = ref(false);
const isCheckingLimit = ref(true); // Loading state for device limit check
const deviceLimitReached = ref(false);
const errorMessage = ref('');
// Get actual device info from trustedDeviceService instead of hardcoding
const detectedDeviceInfo = trustedDeviceService.getDeviceInfo();
const deviceInfo = ref({
  browserInfo: detectedDeviceInfo.browserInfo,
  osInfo: detectedDeviceInfo.osInfo,
  ipAddress: 'Detecting...',
  location: 'Detecting...'
});

// Actions
const handleTrustDevice = async (trust: boolean) => {
  isProcessing.value = true;
  errorMessage.value = '';
  
  try {
    logDebug('Trust device decision', { trust });
    
    if (trust) {
      // Add device to trusted devices
      const result = await trustedDeviceService.addTrustedDevice();
      
      if (result.success) {
        logSuccess('Device added to trusted devices');
        
        // Create audit log for admin login with trusted device
        try {
          const { getCurrentUserFullName } = await import('@/utils/userUtils');
          const performedByName = await getCurrentUserFullName();
          
          await createAuditLog({
            action: 'admin_auth',
            type: 'system_event',
            details: `Admin logged in successfully and added trusted device`,
            performedById: auth.currentUser?.uid,
            performedByName: performedByName,
            performedByEmail: auth.currentUser?.email || '',
            affectedUserId: auth.currentUser?.uid,
            affectedUserName: performedByName,
            affectedUserEmail: auth.currentUser?.email || ''
          });
        } catch (auditError) {
          logError('Failed to create audit log for admin login', auditError);
        }
        
        // Navigate to admin dashboard
        router.push('/admin');
      } else if (result.deviceLimitReached) {
        // Device limit reached - show warning
        logWarn('Device limit reached', { 
          currentCount: result.currentDeviceCount,
          maxDevices: result.maxDevices 
        });
        deviceLimitReached.value = true;
        errorMessage.value = result.message;
        isProcessing.value = false;
        return; // Don't navigate yet, show the limit warning
      } else {
        logError('Failed to add trusted device', new Error(result.message));
        errorMessage.value = result.message || 'Failed to add trusted device';
        isProcessing.value = false;
        return;
      }
    } else {
      logDebug('User declined to trust device');
      
      // Create audit log for admin login (skipped trusted device setup)
      try {
        const { getCurrentUserFullName } = await import('@/utils/userUtils');
        const performedByName = await getCurrentUserFullName();
        
        await createAuditLog({
          action: 'admin_auth',
          type: 'system_event',
          details: `Admin logged in successfully (skipped trusted device setup)`,
          performedById: auth.currentUser?.uid,
          performedByName: performedByName,
          performedByEmail: auth.currentUser?.email || '',
          affectedUserId: auth.currentUser?.uid,
          affectedUserName: performedByName,
          affectedUserEmail: auth.currentUser?.email || ''
        });
      } catch (auditError) {
        logError('Failed to create audit log for admin login', auditError);
      }
      
      // Navigate to admin dashboard
      router.push('/admin');
    }
    
  } catch (error: any) {
    logError('Error handling trust device decision', error);
    errorMessage.value = error.message || 'An error occurred';
    isProcessing.value = false;
  }
};

// Handle redirecting to manage devices when limit is reached
const goToManageDevices = async () => {
  logDebug('Redirecting to device management due to device limit');
  
  // Create audit log for blocked login (device limit reached)
  try {
    await createAuditLog({
      action: 'admin_auth',
      type: 'system_event',
      details: `Admin login blocked - device limit reached (3/3). Redirected to manage devices.`,
      performedById: auth.currentUser?.uid,
      performedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
      performedByEmail: auth.currentUser?.email || '',
      affectedUserId: auth.currentUser?.uid,
      affectedUserName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
      affectedUserEmail: auth.currentUser?.email || ''
    });
  } catch (auditError) {
    logError('Failed to create audit log for blocked login', auditError);
  }
  
  // Store 2FA ID in session storage so we can return to the flow
  if (twoFAId.value) {
    sessionStorage.setItem('pending2FAId', twoFAId.value);
  }
  
  // Redirect to dedicated manage devices page with 2FA ID
  router.push(`/manage-devices?twoFAId=${twoFAId.value}`);
};

// Handle logout when user wants to cancel
const handleLogout = async () => {
  logDebug('User logging out from device limit screen');
  
  try {
    await auth.signOut();
    logSuccess('User logged out successfully');
    router.push('/login');
  } catch (error: any) {
    logError('Error during logout', error);
    // Force redirect to login anyway
    router.push('/login');
  }
};

// Lifecycle
onMounted(async () => {
  if (!twoFAId.value) {
    logError('No 2FA ID provided');
    router.push('/login');
    return;
  }
  
  // Device info is already detected from trustedDeviceService.getDeviceInfo() above
  logDebug('Device info detected', deviceInfo.value);
  
  // Don't fetch IP on client side - let the server detect it
  // The device fingerprint is based on browser/device, not IP
  // So changing location/IP won't affect device trust status
  deviceInfo.value.ipAddress = 'Detecting...';
  deviceInfo.value.location = 'Detecting...';
  
  // Check device limit on page load
  try {
    const deviceCount = await trustedDeviceService.getTrustedDeviceCount();
    logDebug('Device count on trust page', deviceCount);
    
    // If limit is already reached, show the warning immediately
    if (!deviceCount.canAddMore && deviceCount.currentDeviceCount >= deviceCount.maxDevices) {
      logWarn('Device limit already reached on page load', deviceCount);
      deviceLimitReached.value = true;
      errorMessage.value = `Maximum of ${deviceCount.maxDevices} trusted devices allowed. Please remove an existing device first.`;
    }
  } catch (error: any) {
    logError('Error checking device count', error);
    // Don't block the page, just log the error
  } finally {
    isCheckingLimit.value = false;
  }
});
</script>
